import React, { useState } from 'react';
import { ChevronDown, ChevronRight, BarChart3, Layers, Square, LayoutGrid } from 'lucide-react';

export function QuantitiesPanel({
  quantities,
  componentTypes = [],
  walls = [],
  spaces = [],
  onSelectWall,
  onSelectSpace,
  embedded = false,
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('walls'); // 'walls', 'spaces', 'summary'

  if (!quantities) return null;

  const { wallsByType, totalFloorArea, totalVolume } = quantities;

  // Bauteiltyp-Name finden
  const getTypeName = (typeRef) => {
    if (typeRef === 'unassigned') return 'Nicht zugewiesen';
    const type = componentTypes.find(t => t.id === typeRef);
    return type?.shortCode || type?.name || typeRef;
  };

  // Wände für einen Typ finden
  const getWallsForType = (typeRef) => {
    const ref = typeRef === 'unassigned' ? null : typeRef;
    return walls.filter(w => (w.typeRef || null) === ref);
  };

  const content = (
    <>
      {/* Tabs */}
      <div className="flex border-b px-2">
        <TabButton
          active={activeTab === 'walls'}
          onClick={() => setActiveTab('walls')}
          icon={<Layers size={14} />}
          label="Wände"
        />
        <TabButton
          active={activeTab === 'spaces'}
          onClick={() => setActiveTab('spaces')}
          icon={<Square size={14} />}
          label="Räume"
        />
        <TabButton
          active={activeTab === 'summary'}
          onClick={() => setActiveTab('summary')}
          icon={<BarChart3 size={14} />}
          label="Gesamt"
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3">
        {activeTab === 'walls' && (
          <WallQuantities
            wallsByType={wallsByType}
            getTypeName={getTypeName}
            getWallsForType={getWallsForType}
            onSelectWall={onSelectWall}
          />
        )}

        {activeTab === 'spaces' && (
          <SpaceQuantities
            spaces={spaces}
            totalFloorArea={totalFloorArea}
            totalVolume={totalVolume}
            onSelectSpace={onSelectSpace}
          />
        )}

        {activeTab === 'summary' && (
          <SummaryQuantities
            wallsByType={wallsByType}
            totalFloorArea={totalFloorArea}
            totalVolume={totalVolume}
            getTypeName={getTypeName}
          />
        )}
      </div>
    </>
  );

  // Im embedded-Modus direkt den Inhalt zurückgeben
  if (embedded) {
    return content;
  }

  // Standalone mit eigenem Container
  return (
    <div
      className="absolute right-4 bottom-14 w-80 bg-white rounded-lg shadow-lg z-10 max-h-[50vh] overflow-hidden flex flex-col"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full p-3 hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <BarChart3 size={18} className="text-blue-600" />
          <span className="font-semibold text-gray-800">Mengenermittlung</span>
        </div>
        {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </button>

      {isExpanded && content}
    </div>
  );
}

function TabButton({ active, onClick, icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
        active
          ? 'border-blue-500 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function WallQuantities({ wallsByType, getTypeName, getWallsForType, onSelectWall }) {
  const [expandedType, setExpandedType] = useState(null);

  const types = Object.entries(wallsByType || {});
  if (types.length === 0) {
    return <div className="text-gray-400 text-sm">Keine Wände vorhanden</div>;
  }

  return (
    <div className="space-y-2">
      {types.map(([typeRef, data]) => (
        <div key={typeRef} className="border rounded">
          <button
            onClick={() => setExpandedType(expandedType === typeRef ? null : typeRef)}
            className="w-full flex items-center justify-between p-2 hover:bg-gray-50 text-left"
          >
            <div>
              <div className="font-medium text-sm">{getTypeName(typeRef)}</div>
              <div className="text-xs text-gray-500">{data.count} Wände</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium">{data.netArea.toFixed(2)} m²</div>
              <div className="text-xs text-gray-500">{data.totalLength.toFixed(2)} m</div>
            </div>
          </button>

          {expandedType === typeRef && (
            <div className="border-t p-2 bg-gray-50 text-xs space-y-1">
              <div className="grid grid-cols-2 gap-1">
                <div>Bruttofläche:</div>
                <div className="text-right">{data.grossArea.toFixed(2)} m²</div>
                <div>Öffnungen:</div>
                <div className="text-right text-red-600">-{data.openingsArea.toFixed(2)} m²</div>
                {data.deductionsArea > 0 && (
                  <>
                    <div>Schächte/Vorwände:</div>
                    <div className="text-right text-orange-600">-{data.deductionsArea.toFixed(2)} m²</div>
                  </>
                )}
                <div className="font-medium">Nettofläche:</div>
                <div className="text-right font-medium">{data.netArea.toFixed(2)} m²</div>
              </div>

              {/* Einzelne Wände */}
              <div className="mt-2 pt-2 border-t">
                <div className="text-gray-500 mb-1">Einzelwände:</div>
                {getWallsForType(typeRef).map((wall, idx) => (
                  <button
                    key={wall.id}
                    onClick={() => onSelectWall?.(wall.id)}
                    className="w-full text-left px-2 py-1 hover:bg-blue-50 rounded flex justify-between"
                  >
                    <span>Wand {idx + 1}</span>
                    <span className="text-gray-500">
                      {(wall.openings?.length || 0)} Öffn.
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SpaceQuantities({ spaces, totalFloorArea, totalVolume, onSelectSpace }) {
  if (!spaces || spaces.length === 0) {
    return <div className="text-gray-400 text-sm">Keine Räume vorhanden</div>;
  }

  return (
    <div className="space-y-3">
      {/* Gesamtfläche und Volumen */}
      <div className="bg-purple-50 border border-purple-200 rounded p-3">
        <div className="text-sm font-medium text-purple-800 mb-1">
          Gesamte Grundfläche
        </div>
        <div className="text-2xl font-bold text-purple-600">
          {(totalFloorArea || 0).toFixed(2)} m²
        </div>
        <div className="text-xs text-purple-600 mt-1">
          = Deckenfläche (Geschossdecke wird auf Geschoss-Ebene zugewiesen)
        </div>
      </div>

      {/* Gesamtvolumen */}
      <div className="bg-cyan-50 border border-cyan-200 rounded p-3">
        <div className="text-sm font-medium text-cyan-800 mb-1">
          Gesamtes Raumvolumen
        </div>
        <div className="text-2xl font-bold text-cyan-600">
          {(totalVolume || 0).toFixed(2)} m³
        </div>
        <div className="text-xs text-cyan-600 mt-1">
          Grundfläche × Ø Deckenhöhe
        </div>
      </div>

      {/* Einzelne Räume */}
      <div className="border rounded">
        <div className="p-2 bg-gray-50 text-xs font-medium text-gray-500 border-b">
          Einzelne Räume ({spaces.length})
        </div>
        <div className="divide-y">
          {spaces.map((space) => (
            <button
              key={space.id}
              onClick={() => onSelectSpace?.(space.id)}
              className="w-full text-left px-3 py-2 hover:bg-blue-50"
            >
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">{space.name || 'Raum'}</span>
                <span className="text-sm text-gray-500">
                  {(space.floorArea || 0).toFixed(2)} m²
                </span>
              </div>
              {space.volume && (
                <div className="flex justify-between items-center text-xs text-gray-400 mt-1">
                  <span>Ø Höhe: {(space.avgCeilingHeight || 0).toFixed(2)} m</span>
                  <span>Vol: {(space.volume || 0).toFixed(2)} m³</span>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SummaryQuantities({ wallsByType, totalFloorArea, totalVolume, getTypeName }) {
  // Gesamtsummen berechnen
  const totalWalls = Object.values(wallsByType || {}).reduce((sum, d) => ({
    count: sum.count + d.count,
    length: sum.length + d.totalLength,
    grossArea: sum.grossArea + d.grossArea,
    openingsArea: sum.openingsArea + d.openingsArea,
    deductionsArea: sum.deductionsArea + (d.deductionsArea || 0),
    netArea: sum.netArea + d.netArea,
  }), { count: 0, length: 0, grossArea: 0, openingsArea: 0, deductionsArea: 0, netArea: 0 });

  return (
    <div className="space-y-4">
      {/* Wände Gesamt */}
      <div className="border rounded p-3">
        <div className="font-medium text-sm mb-2 flex items-center gap-2">
          <Layers size={16} className="text-orange-500" />
          Wände Gesamt
        </div>
        <div className="grid grid-cols-2 gap-1 text-xs">
          <div>Anzahl:</div>
          <div className="text-right">{totalWalls.count}</div>
          <div>Laufmeter:</div>
          <div className="text-right">{totalWalls.length.toFixed(2)} m</div>
          <div>Bruttofläche:</div>
          <div className="text-right">{totalWalls.grossArea.toFixed(2)} m²</div>
          <div>Öffnungen:</div>
          <div className="text-right text-red-600">-{totalWalls.openingsArea.toFixed(2)} m²</div>
          {totalWalls.deductionsArea > 0 && (
            <>
              <div>Schächte/Vorwände:</div>
              <div className="text-right text-orange-600">-{totalWalls.deductionsArea.toFixed(2)} m²</div>
            </>
          )}
          <div className="font-medium pt-1 border-t">Nettofläche:</div>
          <div className="text-right font-medium pt-1 border-t">{totalWalls.netArea.toFixed(2)} m²</div>
        </div>
      </div>

      {/* Grundfläche (= Deckenfläche) */}
      <div className="border rounded p-3">
        <div className="font-medium text-sm mb-2 flex items-center gap-2">
          <LayoutGrid size={16} className="text-purple-500" />
          Grundfläche / Decke
        </div>
        <div className="grid grid-cols-2 gap-1 text-xs">
          <div className="font-medium">Gesamtfläche:</div>
          <div className="text-right font-medium">{(totalFloorArea || 0).toFixed(2)} m²</div>
        </div>
        <div className="text-xs text-purple-600 mt-2 p-2 bg-purple-50 rounded">
          Deckentyp wird auf Geschoss-Ebene definiert
        </div>
      </div>

      {/* Raumvolumen */}
      <div className="border rounded p-3">
        <div className="font-medium text-sm mb-2 flex items-center gap-2">
          <Square size={16} className="text-cyan-500" />
          Raumvolumen
        </div>
        <div className="grid grid-cols-2 gap-1 text-xs">
          <div className="font-medium">Gesamtvolumen:</div>
          <div className="text-right font-medium">{(totalVolume || 0).toFixed(2)} m³</div>
        </div>
        <div className="text-xs text-cyan-600 mt-2 p-2 bg-cyan-50 rounded">
          Berechnet aus Grundfläche × Ø Deckenhöhe pro Raum
        </div>
      </div>

      {/* Nach Typ aufgeschlüsselt */}
      <div className="border-t pt-3">
        <div className="font-medium text-sm mb-2">Wände nach Bauteiltyp:</div>
        <div className="space-y-1 text-xs">
          {Object.entries(wallsByType || {}).map(([typeRef, data]) => (
            <div key={typeRef} className="flex justify-between">
              <span className="text-gray-600">{getTypeName(typeRef)}:</span>
              <span>{data.netArea.toFixed(2)} m²</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
